/**
 * V2 Profile — data access. Split out of profile.js on 2026-07-25 (it had
 * reached 856 lines against the plan's ~400 budget); the five reads that back
 * the profile view, unchanged. profile.js re-exports every one of them, so
 * this split is invisible to importers.
 */
import * as gateway from '../../core/gateway.js';
import { cached, invalidate } from '../../core/query.js';
import { FEED_SELECT } from '../../ui/card.js';
import { attachPersonalShelves } from '../journal/journal.js';

export async function fetchProfile(username) {
  return cached(`profile:${username.toLowerCase()}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.PUBLIC_PROFILES, {
      select: '*', // V1 user-panel does the same — bio/socials/banner are public
      // ilike without wildcards = case-insensitive equality (usernames are slugs)
      filters: { username: `ilike.${username}` },
      limit: 1,
    });
    if (error) throw error;
    return data?.[0] || null;
  });
}

/**
 * The whole journal, like V1 (user-panel.js loadAllUserYoinks): the
 * category stacks, the stat stamps, and the map all need every yoink,
 * not a page. Batched keyset paging, hard-capped at 3000 rows.
 */
const BATCH = 1000;
const MAX_BATCHES = 3;

export async function fetchAllUserYoinks(userId) {
  return cached(`profile-all-yoinks:${userId}`, async () => {
    const all = [];
    let cursor = null;
    for (let i = 0; i < MAX_BATCHES; i++) {
      const filters = { user_id: `eq.${userId}` };
      if (cursor) {
        filters.or = `(created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id}))`;
      }
      const { data, error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: FEED_SELECT,
        filters,
        order: 'created_at.desc,id.desc',
        limit: BATCH,
      });
      if (error) throw error;
      const batch = data || [];
      all.push(...batch);
      if (batch.length < BATCH) break;
      const last = batch[batch.length - 1];
      cursor = { created_at: last.created_at, id: last.id };
    }
    return attachPersonalShelves(all); // names each personal row's shelf (§11 card row)
  });
}

/** V1's trophy case source (get_user_trophies RPC). Missing RPC → none. */
export async function fetchTrophies(userId) {
  return cached(`profile-trophies:${userId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_user_trophies', {
      params: { p_user_id: userId },
    });
    if (error) throw error;
    return data || [];
  });
}

/**
 * Near-podium standings for the trophy case's "Within reach" strip
 * (2026-07-19): categories where the user ranks 4-6, from the
 * get_user_near_trophies RPC (supabase/migrations/20260719c_near_trophies.sql). Until that
 * SQL runs the RPC 404s and the strip quietly stays away — the
 * lists/trophies degradation pattern. Own-profile only; callers never
 * fetch this for visitors.
 */
export async function fetchNearTrophies(userId) {
  return cached(`profile-near-trophies:${userId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_user_near_trophies', {
      params: { p_user_id: userId },
    });
    if (error) throw error;
    return data || [];
  });
}

/**
 * Every climbable standing for the "Within reach" strip's two groups
 * (2026-08-07): ranks 2-6 with the NEXT RUNG UP as the target — silver
 * aims at gold, bronze at silver, ranks 4-6 at bronze. From the
 * get_user_trophy_gaps RPC (supabase/migrations/20260807b_trophy_gaps.sql).
 * Until that SQL runs the RPC 404s and callers fall back to
 * fetchNearTrophies (ranks 4-6 only). Own-profile only.
 */
export async function fetchTrophyGaps(userId) {
  return cached(`profile-trophy-gaps:${userId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_user_trophy_gaps', {
      params: { p_user_id: userId },
    });
    if (error) throw error;
    return data || [];
  });
}

/**
 * The site-wide medal tally, Olympic ordering (gold, then silver, then
 * bronze), from the get_medal_table RPC
 * (supabase/migrations/20260807a_medal_table.sql). Until that SQL runs
 * the RPC 404s and the /medals page shows its quiet empty state.
 * Public data — the page is viewable logged out.
 */
export async function fetchMedalTable() {
  return cached('medal-table', async () => {
    const { data, error } = await gateway.rawRpc('get_medal_table', {
      params: { p_limit: 50 },
    });
    if (error) throw error;
    return data || [];
  });
}

/**
 * The owner's story for one CATEGORY — the "let me tell you what makes
 * this special" text on a ?category=-filtered journal (2026-07-26,
 * landing-pages push step 2; supabase/migrations/20260726b_collection_stories.sql).
 * Until that SQL runs the table 404s, this throws, and callers let the
 * story quietly stay away — the standing degradation pattern.
 */
export async function fetchCollectionStory(userId, categoryId) {
  return cached(`collection-story:${userId}:${categoryId}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.USER_COLLECTION_STORIES, {
      select: 'story',
      filters: { user_id: `eq.${userId}`, category_id: `eq.${categoryId}` },
      limit: 1,
    });
    if (error) throw error;
    return data?.[0]?.story || '';
  });
}

/**
 * Save (or clear, with empty text) the owner's story for a category.
 * "Collection" survives in the name and the table only because
 * user_collection_stories is applied by hand and cannot be renamed from
 * here; the row hangs off a category_id, not an is_set collection.
 * Update-then-insert rather than an upsert header: rawInsert has no
 * merge-duplicates support, and the row is only ever the owner's own,
 * so the race window is their own two thumbs.
 */
export async function saveCollectionStory(userId, categoryId, story) {
  const text = (story || '').trim();
  const filters = { user_id: `eq.${userId}`, category_id: `eq.${categoryId}` };
  if (!text) {
    const { error } = await gateway.rawDelete(gateway.TABLES.USER_COLLECTION_STORIES, { filters });
    if (error) throw error;
  } else {
    const { data, error } = await gateway.rawUpdate(gateway.TABLES.USER_COLLECTION_STORIES, {
      filters,
      body: { story: text, updated_at: new Date().toISOString() },
    });
    if (error) throw error;
    if (!data?.length) {
      const { error: insertError } = await gateway.rawInsert(gateway.TABLES.USER_COLLECTION_STORIES, {
        body: { user_id: userId, category_id: categoryId, story: text },
      });
      if (insertError) throw insertError;
    }
  }
  invalidate(`collection-story:${userId}:${categoryId}`);
  return text;
}

/** Started collections (V1 Sets) with progress, via V1's RPC. */
export async function fetchStartedSets(userId) {
  return cached(`started-sets:${userId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_user_started_sets', {
      params: { p_user_id: userId },
    });
    if (error) throw error;
    return data || [];
  });
}
